import { assertNever } from '@gitbook/util-ts';

type NotificationSpaceTopic = {
    type: 'space';
    space: string;
};

type NotificationCollectionTopic = {
    type: 'collection';
    collection: string;
};

type NotificationChangeRequestTopic = {
    type: 'change_request';
    space: string;
    changeRequest: string;
};

type NotificationCommentTopic = {
    type: 'comment';
    comment: string;
    space: string;
};

type NotificationOrganizationTopic = {
    type: 'organization';
    organization: string;
};

type NotificationSiteTopic = {
    type: 'site';
    site: string;
};

/**
 * Different topics for a notification.
 */
export type NotificationTopic =
    | NotificationSpaceTopic
    | NotificationCollectionTopic
    | NotificationChangeRequestTopic
    | NotificationCommentTopic
    | NotificationOrganizationTopic
    | NotificationSiteTopic;

/**
 * Returns the topic string for a notification.
 */
export function getNotificationTopic(topic: NotificationTopic): string {
    switch (topic.type) {
        case 'space':
            return `space/${topic.space}`;
        case 'collection':
            return `collection/${topic.collection}`;
        case 'change_request':
            return `space/${topic.space}/cr/${topic.changeRequest}`;
        case 'comment':
            return `space/${topic.space}/comment/${topic.comment}`;
        case 'organization':
            return `organization/${topic.organization}`;
        case 'site':
            return `site/${topic.site}`;
        default:
            assertNever(topic);
    }
}

/**
 * Parse a notification topic, returning the well-defined notification object. Returns
 * null if it cannot be parsed.
 */
export function parseNotificationTopic(input: string): NotificationTopic | null {
    const parts = input.split('/');

    switch (parts[0]) {
        case 'space':
            if (parts[2] === 'cr') {
                return {
                    type: 'change_request',
                    space: parts[1],
                    changeRequest: parts[3],
                };
            }

            if (parts[2] === 'comment') {
                return {
                    type: 'comment',
                    space: parts[1],
                    comment: parts[3],
                };
            }

            return null;
        case 'collection':
            return {
                type: 'collection',
                collection: parts[1],
            };
        case 'organization':
            return {
                type: 'organization',
                organization: parts[1],
            };
        case 'site':
            return {
                type: 'site',
                site: parts[1],
            };
    }

    return null;
}
